import {
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  Checkbox,
  FormGroup,
  FormHelperText,
} from '@mui/material'
import { useState, useEffect, CSSProperties } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import KumocanTextareaAutosize from 'components/textArea'
import KanseiTextField from 'components/textField'
import ThankYouMessage from 'components/ThankYouMessage'
import { UndesiredWorkConditionData } from 'types/insight'

type CategoryType =
  | 'coWorker'
  | 'workSite'
  | 'businessDivision'
  | 'distance'
  | 'numberOfCoWorkers'
  | 'pattern'
  | 'other'

const styles: Record<string, CSSProperties> = {
  container: {
    color: '#333',
    padding: '24px 20px 40px',
    margin: '0 auto',
    maxWidth: 480,
    fontSize: 16,
    lineHeight: 1.6,
    boxSizing: 'border-box',
    backgroundColor: '#f8f9fa',
    minHeight: '100vh',
  },
  title: {
    fontSize: 22,
    fontWeight: 700,
    textAlign: 'center',
    margin: '0 0 24px',
  },
  section: {
    marginBottom: 16,
    padding: 16,
    backgroundColor: '#fff',
    borderRadius: 12,
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  sectionSelected: {
    backgroundColor: 'rgba(20, 183, 174, 0.08)',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    borderRadius: '12px 12px 0 0',
    marginBottom: 0,
  },
  sectionContent: {
    display: 'flex',
    alignItems: 'center',
    flex: 1,
  },
  sectionIcon: {
    marginRight: 12,
    fontSize: 24,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: 600,
    marginBottom: 4,
    color: '#333',
  },
  sectionSubtitle: {
    fontSize: 16,
    color: '#666',
    marginBottom: 0,
  },
  arrow: {
    fontSize: 20,
    color: '#ccc',
  },
  description: {
    fontSize: 16,
    color: '#666',
    marginBottom: 16,
  },
  fieldLabel: {
    fontSize: 16,
    fontWeight: 500,
    marginBottom: 8,
  },
  fieldContainer: {
    marginTop: 0,
    marginBottom: 16,
    padding: '20px',
    backgroundColor: '#fff',
    borderRadius: '0 0 12px 12px',
    border: 'none',
    boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
  },
  selectBoxContainer: {
    marginTop: 0,
    marginBottom: 16,
    padding: '20px',
    backgroundColor: '#fff',
    borderRadius: '0 0 12px 12px',
    border: 'none',
    boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
  },
  checkboxGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  radioGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  radioOption: {
    display: 'flex',
    alignItems: 'center',
    padding: '8px 12px',
    backgroundColor: '#f8f9fa',
    borderRadius: 8,
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
  },
  radioOptionSelected: {
    backgroundColor: '#e3f2fd',
  },
  radioInput: {
    marginRight: 8,
  },
  btnWrap: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: 20,
  },
  btnPrimary: {
    width: '90%',
    maxWidth: 360,
    height: 60,
    background: '#1976d2',
    color: '#fff',
    fontSize: 20,
    fontWeight: 700,
    border: 'none',
    borderRadius: 12,
    boxShadow: '0 2px 6px rgba(0,0,0,.15)',
    cursor: 'pointer',
    transition: 'opacity .2s ease, filter .2s ease',
  },
  btnDisabled: {
    opacity: 0.5,
    filter: 'grayscale(100%)',
    cursor: 'default',
    boxShadow: 'none',
    pointerEvents: 'none',
  },
  doneMsg: {
    textAlign: 'center',
    fontSize: 16,
    marginTop: 8,
  },
  notes: {
    fontSize: 13,
    lineHeight: 1.5,
  },
  fieldDescription: {
    fontSize: 16,
    color: '#666',
    marginBottom: 12,
    lineHeight: 1.4,
  },
}

interface Props {
  onSubmit: (data: UndesiredWorkConditionData) => Promise<void>
  isSubmitted?: boolean
  initialData?: Partial<UndesiredWorkConditionData>
}

export default function UndesiredWorkConditionForm({
  onSubmit,
  isSubmitted = false,
  initialData,
}: Props) {
  const { t } = useTranslation('page-insight-undesired-work')
  const [selectedCategory, setSelectedCategory] = useState<CategoryType | null>(
    null,
  )

  const businessDivisionOptions = [
    {
      value: '土木',
      label: t('categories.business_division.civil_engineering'),
    },
    { value: '建築', label: t('categories.business_division.construction') },
    {
      value: '片交',
      label: t('categories.business_division.one_way_traffic'),
    },
    {
      value: '通行止め',
      label: t('categories.business_division.road_closure'),
    },
  ]

  const numberOfCoWorkersOptions = [
    {
      value: 'alone',
      label: t('categories.number_of_co_workers.alone'),
    },
    {
      value: 'multi',
      label: t('categories.number_of_co_workers.multi'),
    },
  ]

  const patternOptions = [
    { value: 'fixed', label: t('categories.pattern.fixed') },
    { value: 'fluid', label: t('categories.pattern.fluid') },
  ]

  const {
    control,
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm<UndesiredWorkConditionData>({
    defaultValues: {
      coWorker: initialData?.coWorker || null,
      workSite: initialData?.workSite || null,
      businessDivision: initialData?.businessDivision || [],
      distance: initialData?.distance || null,
      numberOfCoWorkers: initialData?.numberOfCoWorkers || null,
      numberOfCoWorkersDetail: initialData?.numberOfCoWorkersDetail || null,
      pattern: initialData?.pattern || null,
      other: initialData?.other || null,
    },
  })

  const watchNumberOfCoWorkers = watch('numberOfCoWorkers')

  // numberOfCoWorkersが'alone'になったときにnumberOfCoWorkersDetailをクリア
  useEffect(() => {
    if (watchNumberOfCoWorkers === 'alone') {
      reset((prev) => ({
        ...prev,
        numberOfCoWorkersDetail: null,
      }))
    }
  }, [watchNumberOfCoWorkers, reset])

  const handleCategorySelect = (category: CategoryType) => {
    if (isSubmitted) return

    // 新しいカテゴリを選択した際に、フォームをリセットして該当フィールドのみ保持
    if (selectedCategory !== category) {
      reset({
        coWorker: null,
        workSite: null,
        businessDivision: [],
        distance: null,
        numberOfCoWorkers: null,
        numberOfCoWorkersDetail: null,
        pattern: null,
        other: null,
      })
    }

    setSelectedCategory(category)
  }

  const handleFormSubmit = (data: UndesiredWorkConditionData) => {
    if (isSubmitted) return

    // 選択されたカテゴリ以外のデータをクリア

    const cleanedData: UndesiredWorkConditionData = {
      coWorker: selectedCategory === 'coWorker' ? data.coWorker : null,
      workSite: selectedCategory === 'workSite' ? data.workSite : null,
      businessDivision:
        selectedCategory === 'businessDivision' ? data.businessDivision : [],
      distance: selectedCategory === 'distance' ? Number(data.distance) : null,
      numberOfCoWorkers:
        selectedCategory === 'numberOfCoWorkers'
          ? data.numberOfCoWorkers
          : null,
      numberOfCoWorkersDetail:
        selectedCategory === 'numberOfCoWorkers'
          ? Number(data.numberOfCoWorkersDetail)
          : null,
      pattern: selectedCategory === 'pattern' ? data.pattern : null,
      other: selectedCategory === 'other' ? data.other : null,
    }

    onSubmit(cleanedData)
  }

  const btnStyle = (): CSSProperties => {
    return isSubmitted
      ? { ...styles.btnPrimary, ...styles.btnDisabled }
      : styles.btnPrimary
  }

  if (isSubmitted) {
    return <ThankYouMessage />
  }

  return (
    <div style={styles.container}>
      <h1 style={styles.title}>{t('title')}</h1>

      <p style={styles.description}>
        <strong>{t('instruction')}</strong>
      </p>

      <form onSubmit={handleSubmit(handleFormSubmit)}>
        {/* 相勤者 */}
        <div
          style={{
            ...styles.section,
            ...(selectedCategory === 'coWorker' ? styles.sectionSelected : {}),
          }}
          onClick={() => handleCategorySelect('coWorker')}
        >
          <div style={styles.sectionContent}>
            <span style={{ ...styles.sectionIcon }}>👥</span>
            <div>
              <div style={styles.sectionTitle}>
                {t('categories.co_worker.title')}
              </div>
              <div style={styles.sectionSubtitle}>
                {t('categories.co_worker.subtitle')}
              </div>
            </div>
          </div>
          <span style={styles.arrow}>›</span>
        </div>
        {selectedCategory === 'coWorker' && (
          <div style={styles.fieldContainer}>
            <KanseiTextField
              id="coWorker"
              label={t('categories.co_worker.placeholder')}
              register={register('coWorker', {
                required: t('validation.input_required'),
              })}
              maxLength={1000}
              errors={errors}
            />
          </div>
        )}

        {/* 顧客 */}
        <div
          style={{
            ...styles.section,
            ...(selectedCategory === 'workSite' ? styles.sectionSelected : {}),
          }}
          onClick={() => handleCategorySelect('workSite')}
        >
          <div style={styles.sectionContent}>
            <span style={{ ...styles.sectionIcon }}>🏢</span>

            <div>
              <div style={styles.sectionTitle}>
                {t('categories.work_site.title')}
              </div>
              <div style={styles.sectionSubtitle}>
                {t('categories.work_site.subtitle')}
              </div>
            </div>
          </div>
          <span style={styles.arrow}>›</span>
        </div>
        {selectedCategory === 'workSite' && (
          <div style={styles.fieldContainer}>
            <KanseiTextField
              id="workSite"
              label={t('categories.work_site.placeholder')}
              register={register('workSite', {
                required: t('validation.input_required'),
              })}
              errors={errors}
              maxLength={1000}
            />
          </div>
        )}

        {/* 業務内容 */}
        <div
          style={{
            ...styles.section,
            ...(selectedCategory === 'businessDivision'
              ? styles.sectionSelected
              : {}),
          }}
          onClick={() => handleCategorySelect('businessDivision')}
        >
          <div style={styles.sectionContent}>
            <span style={{ ...styles.sectionIcon }}>🚧</span>
            <div>
              <div style={styles.sectionTitle}>
                {t('categories.business_division.title')}
              </div>
              <div style={styles.sectionSubtitle}>
                {t('categories.business_division.subtitle')}
              </div>
            </div>
          </div>
          <span style={styles.arrow}>›</span>
        </div>
        {selectedCategory === 'businessDivision' && (
          <div style={styles.selectBoxContainer}>
            <div style={styles.fieldDescription}>
              {t('categories.business_division.description')}
            </div>
            <Controller
              name="businessDivision"
              control={control}
              rules={{ required: t('validation.select_required') }}
              render={({ field }) => (
                <FormControl
                  error={!!errors.businessDivision}
                  component="fieldset"
                >
                  <FormGroup>
                    {businessDivisionOptions.map((option) => (
                      <FormControlLabel
                        key={option.value}
                        control={
                          <Checkbox
                            checked={(field.value || []).includes(option.value)}
                            onChange={(e) => {
                              const currentValues = field.value || []
                              if (e.target.checked) {
                                field.onChange([...currentValues, option.value])
                              } else {
                                field.onChange(
                                  currentValues.filter(
                                    (v) => v !== option.value,
                                  ),
                                )
                              }
                            }}
                            value={option.value}
                          />
                        }
                        label={option.label}
                        sx={{
                          '& .MuiFormControlLabel-label': { fontSize: 16 },
                        }}
                      />
                    ))}
                  </FormGroup>
                  {errors.businessDivision && (
                    <FormHelperText>
                      {errors.businessDivision.message}
                    </FormHelperText>
                  )}
                </FormControl>
              )}
            />
          </div>
        )}

        {/* 現場条件（通勤時間） */}
        <div
          style={{
            ...styles.section,
            ...(selectedCategory === 'distance' ? styles.sectionSelected : {}),
          }}
          onClick={() => handleCategorySelect('distance')}
        >
          <div style={styles.sectionContent}>
            <span style={{ ...styles.sectionIcon }}>📍</span>
            <div>
              <div style={styles.sectionTitle}>
                {t('categories.distance.title')}
              </div>
              <div style={styles.sectionSubtitle}>
                {t('categories.distance.subtitle')}
              </div>
            </div>
          </div>
          <span style={styles.arrow}>›</span>
        </div>
        {selectedCategory === 'distance' && (
          <div style={styles.fieldContainer}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <div style={{ flex: 1 }}>
                <KanseiTextField
                  id="distance"
                  type="number"
                  register={register('distance', {
                    required: t('validation.input_required'),
                    max: {
                      value: 360,
                      message: t('validation.max_minutes', { max: 360 }),
                    },
                    min: {
                      value: 1,
                      message: t('validation.min_minutes', { min: 1 }),
                    },
                  })}
                  errors={errors}
                  label="例) 30"
                />
              </div>
              <div
                style={{
                  fontSize: 16,
                  color: '#666',
                  whiteSpace: 'nowrap',
                }}
              >
                {t('categories.distance.unit')}
              </div>
            </div>
          </div>
        )}

        {/* 勤務人数 */}
        <div
          style={{
            ...styles.section,
            ...(selectedCategory === 'numberOfCoWorkers'
              ? styles.sectionSelected
              : {}),
          }}
          onClick={() => handleCategorySelect('numberOfCoWorkers')}
        >
          <div style={styles.sectionContent}>
            <span style={{ ...styles.sectionIcon }}>👥</span>
            <div>
              <div style={styles.sectionTitle}>
                {t('categories.number_of_co_workers.title')}
              </div>
              <div style={styles.sectionSubtitle}>
                {t('categories.number_of_co_workers.subtitle')}
              </div>
            </div>
          </div>
          <span style={styles.arrow}>›</span>
        </div>
        {selectedCategory === 'numberOfCoWorkers' && (
          <div style={styles.selectBoxContainer}>
            <div style={styles.fieldDescription}>
              {t('categories.number_of_co_workers.description')}
            </div>
            <Controller
              name="numberOfCoWorkers"
              control={control}
              rules={{ required: t('validation.select_required') }}
              render={({ field }) => (
                <FormControl error={!!errors.numberOfCoWorkers}>
                  <RadioGroup {...field}>
                    {numberOfCoWorkersOptions.map((option) => (
                      <FormControlLabel
                        key={option.value}
                        value={option.value}
                        control={<Radio />}
                        label={option.label}
                        sx={{
                          '& .MuiFormControlLabel-label': { fontSize: 16 },
                        }}
                      />
                    ))}
                  </RadioGroup>
                  {errors.numberOfCoWorkers && (
                    <FormHelperText>
                      {errors.numberOfCoWorkers.message}
                    </FormHelperText>
                  )}
                </FormControl>
              )}
            />
            {watchNumberOfCoWorkers === 'multi' && (
              <div
                style={{
                  marginTop: 16,
                  padding: 16,
                  backgroundColor: '#f8f9fa',
                  borderRadius: 8,
                  border: '1px dashed #ddd',
                }}
              >
                <div
                  style={{
                    fontSize: 14,
                    color: '#666',
                    marginBottom: 12,
                    fontWeight: 500,
                  }}
                >
                  {t('categories.number_of_co_workers.detail_description')}
                </div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <KanseiTextField
                      id="numberOfCoWorkersDetail"
                      type="number"
                      register={register('numberOfCoWorkersDetail', {
                        required: t('validation.enter_count'),
                        min: {
                          value: 2,
                          message: t('validation.min_people', { min: 2 }),
                        },
                      })}
                      errors={errors}
                      label="例) 5"
                    />
                  </div>
                  <div
                    style={{
                      fontSize: 16,
                      color: '#666',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {t('categories.number_of_co_workers.unit')}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* 配置パターン */}
        <div
          style={{
            ...styles.section,
            ...(selectedCategory === 'pattern' ? styles.sectionSelected : {}),
          }}
          onClick={() => handleCategorySelect('pattern')}
        >
          <div style={styles.sectionContent}>
            <span style={{ ...styles.sectionIcon }}>🗺️</span>
            <div>
              <div style={styles.sectionTitle}>
                {t('categories.pattern.title')}
              </div>
              <div style={styles.sectionSubtitle}>
                {t('categories.pattern.subtitle')}
              </div>
            </div>
          </div>
          <span style={styles.arrow}>›</span>
        </div>
        {selectedCategory === 'pattern' && (
          <div style={styles.selectBoxContainer}>
            <Controller
              name="pattern"
              control={control}
              rules={{ required: t('validation.select_required') }}
              render={({ field }) => (
                <FormControl error={!!errors.pattern}>
                  <RadioGroup {...field}>
                    {patternOptions.map((option) => (
                      <FormControlLabel
                        key={option.value}
                        value={option.value}
                        control={<Radio />}
                        label={option.label}
                        sx={{
                          '& .MuiFormControlLabel-label': { fontSize: 16 },
                        }}
                      />
                    ))}
                  </RadioGroup>
                  {errors.pattern && (
                    <FormHelperText>{errors.pattern.message}</FormHelperText>
                  )}
                </FormControl>
              )}
            />
          </div>
        )}

        {/* その他 */}
        <div
          style={{
            ...styles.section,
            ...(selectedCategory === 'other' ? styles.sectionSelected : {}),
          }}
          onClick={() => handleCategorySelect('other')}
        >
          <div style={styles.sectionContent}>
            <span style={{ ...styles.sectionIcon }}>💭</span>
            <div>
              <div style={styles.sectionTitle}>
                {t('categories.other.title')}
              </div>
              <div style={styles.sectionSubtitle}>
                {t('categories.other.subtitle')}
              </div>
            </div>
          </div>
          <span style={styles.arrow}>›</span>
        </div>
        {selectedCategory === 'other' && (
          <div style={styles.fieldContainer}>
            <KumocanTextareaAutosize
              id="other"
              register={register('other', {
                required: t('validation.input_required'),
              })}
              placeholder={t('categories.other.placeholder')}
              style={{
                width: '100%',
                minHeight: 100,
                padding: 12,
                border: '1px solid #ccc',
                borderRadius: 4,
                fontSize: 16,
                fontFamily: 'inherit',
                boxSizing: 'border-box',
              }}
            />
            {errors.other && (
              <FormHelperText sx={{ color: 'red' }}>
                {errors.other.message}
              </FormHelperText>
            )}
          </div>
        )}

        {selectedCategory && (
          <div style={styles.btnWrap}>
            <button
              type="submit"
              disabled={isSubmitted}
              aria-disabled={isSubmitted}
              style={btnStyle()}
            >
              {t('submit_button')}
            </button>
          </div>
        )}
      </form>

      <div style={styles.notes}>
        <p>{t('note_reference')}</p>
        <p>{t('note_adjustment')}</p>
      </div>
    </div>
  )
}
